import { Component, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import {
  ActionHistoryService,
  PackagingDailyKpiResponse,
  PackagingKpiResponse
} from 'src/app/services/actionHistory/action-history.service';
import * as XLSX from 'xlsx';

type PackagingKpiTab = 'summary' | 'daily';

@Component({
  selector: 'app-packaging-kpi',
  templateUrl: './packaging-kpi.component.html',
  styleUrls: ['./packaging-kpi.component.css']
})
export class PackagingKpiComponent implements OnInit {
  readonly availablePackagers: string[] = [
    'Carlos', 'Hiler', 'Maxi', 'Leonardo',
    'Empaquetador 1', 'Empaquetador 2', 'Empaquetador 3'
  ];

  activeTab: PackagingKpiTab = 'summary';
  kpiData: PackagingKpiResponse | null = null;
  dailyKpiData: PackagingDailyKpiResponse | null = null;
  loading = false;
  errorMessage = '';

  fromDate = '';
  toDate = '';
  selectedPackager: string | null = null;

  constructor(private actionHistoryService: ActionHistoryService) {}

  ngOnInit(): void {
    this.initializeDates();
    this.loadData();
  }

  setActiveTab(tab: PackagingKpiTab): void {
    this.activeTab = tab;
  }

  applyFilters(): void {
    this.loadData();
  }

  clearFilters(): void {
    this.selectedPackager = null;
    this.initializeDates();
    this.loadData();
  }

  exportSummaryToExcel(): void {
    if (!this.kpiData) {
      return;
    }

    const rows: any[][] = [
      ['Resumen KPI Empaquetado'],
      ['Desde', this.fromDate || ''],
      ['Hasta', this.toDate || ''],
      ['Empaquetador filtrado', this.getSelectedPackagerName()],
      [],
      ['KPIs generales'],
      ['Paquetes totales', this.kpiData.totalPackages],
      ['Piezas totales', this.kpiData.totalSpareParts],
      ['Promedio piezas/paquete', this.kpiData.averagePartsPerPackage],
      ['Productividad diaria', this.kpiData.dailyProductivityPackages],
      [],
      ['Resumen por empaquetador'],
      ['Empaquetador', 'Paquetes', 'Piezas', 'Promedio piezas/paquete', 'Productividad diaria']
    ];

    this.kpiData.packagers.forEach(item => {
      rows.push([
        item.packager,
        item.packagesCount,
        item.sparePartsCount,
        item.averagePartsPerPackage,
        item.dailyProductivityPackages
      ]);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Resumen');

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    XLSX.writeFile(workbook, `kpis-empaquetado-resumen-${timestamp}.xlsx`);
  }

  exportDailyToExcel(): void {
    if (!this.dailyKpiData) {
      return;
    }

    const metaRows: any[][] = [
      ['Resumen por día - Empaquetado'],
      ['Desde', this.fromDate || ''],
      ['Hasta', this.toDate || ''],
      ['Empaquetador filtrado', this.getSelectedPackagerName()],
      []
    ];

    const detailRows: any[][] = [
      ...metaRows,
      ['Detalle por día y empaquetador'],
      ['Fecha', 'Empaquetador', 'Paquetes', 'Piezas', 'Promedio piezas/paquete']
    ];
    this.dailyKpiData.rows.forEach(item => {
      detailRows.push([
        this.formatDateForDisplay(item.date),
        item.packager,
        item.packagesCount,
        item.sparePartsCount,
        item.averagePartsPerPackage
      ]);
    });

    const totalsRows: any[][] = [
      ...metaRows,
      ['Totales por día'],
      ['Fecha', 'Paquetes', 'Piezas', 'Promedio piezas/paquete']
    ];
    this.dailyKpiData.totalsByDate.forEach(item => {
      totalsRows.push([
        this.formatDateForDisplay(item.date),
        item.packagesCount,
        item.sparePartsCount,
        item.averagePartsPerPackage
      ]);
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(detailRows), 'Detalle por dia');
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(totalsRows), 'Totales por dia');

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    XLSX.writeFile(workbook, `kpis-empaquetado-por-dia-${timestamp}.xlsx`);
  }

  canExportSummary(): boolean {
    return !this.loading && !!this.kpiData;
  }

  canExportDaily(): boolean {
    return !this.loading && !!this.dailyKpiData;
  }

  formatDateForDisplay(value: string | undefined): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const day = `${date.getUTCDate()}`.padStart(2, '0');
    const month = `${date.getUTCMonth() + 1}`.padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
  }

  private loadData(): void {
    this.loading = true;
    this.errorMessage = '';

    const params = {
      fromDate: this.fromDate || undefined,
      toDate: this.toDate || undefined,
      packager: this.selectedPackager || undefined
    };

    forkJoin({
      summary: this.actionHistoryService.getPackagingKpis(params),
      daily: this.actionHistoryService.getPackagingDailyKpis(params)
    }).subscribe({
      next: ({ summary, daily }) => {
        this.kpiData = summary;
        this.dailyKpiData = daily;
      },
      error: (error) => {
        this.kpiData = null;
        this.dailyKpiData = null;
        this.errorMessage = error?.error?.message || error?.error?.error || 'No se pudieron cargar los KPIs.';
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  private initializeDates(): void {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 7);

    this.fromDate = this.formatDateForInput(from);
    this.toDate = this.formatDateForInput(to);
  }

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getSelectedPackagerName(): string {
    if (!this.selectedPackager) {
      return 'Todos';
    }

    return this.selectedPackager;
  }
}
